import { resolveLoginErrorFromApiError } from '@/lib/auth/login-api-error-messages';
import { SUPPLIER_APP_NAME } from '@/lib/constants/app.constants';
import { AppError, AuthenticationError, NetworkError } from '@/lib/errors/error-types';
import { logger } from '@/lib/logger';
import { ApiError, apiRequest } from '@/services/api/client';
import { API_ENDPOINTS } from '@/services/api/endpoints';
import { systemConfig } from '@/config/system.config';
import type { LoginResponse } from './login-result.interface';

const AUTH_LOG = '[supplier-auth]';

interface LoginRequestBody {
  token?: string;
  tenantId: string;
  appName: typeof SUPPLIER_APP_NAME;
}

function resolveLoginApiErrorMessage(error: ApiError): string {
  return resolveLoginErrorFromApiError(error);
}

/**
 * Normalizes unknown errors from supplier-portal sign-in flows for UI display.
 */
export function resolveSupplierPortalLoginError(err: unknown): string {
  if (err instanceof AuthenticationError || err instanceof NetworkError) {
    return err.message;
  }
  if (err instanceof AppError) {
    return err.message;
  }
  if (err instanceof ApiError) {
    return resolveLoginApiErrorMessage(err);
  }
  if (err instanceof Error) {
    const m = err.message.trim();
    return m || 'An unexpected error occurred during sign in. Please try again.';
  }
  return 'An unexpected error occurred during sign in. Please try again.';
}

function handleLoginError(error: unknown): never {
  if (error instanceof ApiError) {
    logger.error('Login API error', error instanceof Error ? error : new Error(String(error)));
    if (error.status === 401 || error.status === 403 || error.status === 423) {
      const message = resolveLoginApiErrorMessage(error);
      const details = error.data ? (error.data as Record<string, unknown>) : undefined;
      throw new AuthenticationError(message, details);
    }
    const message = resolveLoginApiErrorMessage(error);
    throw new ApiError(message, error.status, error.data);
  }
  if (error instanceof TypeError && error.message.includes('fetch')) {
    logger.error('Network error during login', error);
    throw new NetworkError(
      'Unable to connect to the server. Please check your internet connection and try again.'
    );
  }
  if (error instanceof AuthenticationError || error instanceof NetworkError) {
    throw error;
  }
  const err = error instanceof Error ? error : new Error(String(error));
  logger.error('Unexpected login error', err);
  throw new AuthenticationError('An unexpected error occurred during sign in. Please try again.');
}

/**
 * Exchanges a B2C id_token for an application JWT using the build-time Pepkor tenant.
 */
export async function login(idToken: string): Promise<LoginResponse> {
  try {
    const body: LoginRequestBody = {
      token: idToken,
      tenantId: systemConfig.tenantId,
      appName: SUPPLIER_APP_NAME,
    };
    logger.debug(AUTH_LOG + ' login(): POST /auth/login', {
      tenantId: systemConfig.tenantId,
      appName: SUPPLIER_APP_NAME,
      idTokenLength: idToken.length,
    });
    const { data } = await apiRequest<LoginResponse>({
      url: API_ENDPOINTS.auth.login,
      method: 'POST',
      body,
      skipAuth: true,
    });
    if (!data?.accessToken || !data.user || !data.tenantId) {
      logger.error('Invalid login response: missing accessToken, user, or tenantId');
      throw new AuthenticationError('Invalid login response. Please try signing in again.');
    }
    if (data.defaultTenant) {
      logger.debug(AUTH_LOG + ' login(): default tenant from API', {
        defaultTenant: data.defaultTenant,
      });
    }
    return data;
  } catch (error) {
    return handleLoginError(error);
  }
}

/**
 * Builds the Azure AD B2C login URL with redirect_uri from injected system config.
 */
export function getB2CLoginUrl(): string | null {
  try {
    const loginUrl = systemConfig.getB2CLoginUrl();
    if (!loginUrl) {
      logger.error('B2C login URL is not configured');
      return null;
    }
    logger.debug(AUTH_LOG + ' getB2CLoginUrl(): ready');
    return loginUrl;
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    logger.error('Unexpected error building B2C login URL', err);
    throw new Error('Failed to load login configuration. Please refresh the page and try again.');
  }
}

/**
 * Attempts SSO silent login using an existing httpOnly SSO cookie.
 */
export async function trySsoSilentLogin(): Promise<LoginResponse | null> {
  try {
    logger.debug(AUTH_LOG + ' trySsoSilentLogin(): POST /auth/login', {
      tenantId: systemConfig.tenantId,
      appName: SUPPLIER_APP_NAME,
    });
    const { data } = await apiRequest<LoginResponse>({
      url: API_ENDPOINTS.auth.login,
      method: 'POST',
      body: {
        tenantId: systemConfig.tenantId,
        appName: SUPPLIER_APP_NAME,
      },
      skipAuth: true,
    });
    if (data?.accessToken && data.user && data.tenantId) {
      return data;
    }
    return null;
  } catch (err) {
    logger.debug(AUTH_LOG + ' trySsoSilentLogin(): failed', { err: String(err) });
    return null;
  }
}

/**
 * Calls the logout endpoint and clears the server session cookie when applicable.
 */
export async function logout(): Promise<void> {
  try {
    await apiRequest({
      url: API_ENDPOINTS.auth.logout,
      method: 'GET',
    });
  } catch (error) {
    if (error instanceof ApiError) {
      logger.warn('Logout API call failed', { status: error.status, message: error.message });
    } else {
      const err = error instanceof Error ? error : new Error(String(error));
      logger.error('Unexpected logout error', err);
    }
  }
}
